import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Pagination,
  PaginationItem,
} from "@mui/material";
import { baseFetch } from "../../helpers/apiClient";
import useDashboardList from "../../hooks/useDashboardList";
import { useDispatch, useSelector } from "react-redux";
import { toggleSessionModal } from "../../redux/sessionSlice";
import Loader from "../../components/Loader";
import DeleteEventPopup from "../../components/DeleteEventPopup";
import Background from "../../components/Background";
import { formatEventType } from "../../constants/RegionalLocationList";
import useDebounce from "../../hooks/useDebounce";
import MenteeHeader from "../../components/MenteeHeader";
import { NextSlot, PreviousSlot } from "../Dashboard/IndustryHub";
import Circle from "../../assets/images/Ellipse 23448.svg";
import tickMark from "../../assets/images/Vector 169 (1).svg";
import { useNavigate } from "react-router-dom";

function showDateAndTime(event) {
  if (event.eventType === "webinar" || event.eventType === "virtual-event") {
    return true;
  } else {
    return false;
  }
}

const paginationSlots = {
  previous: PreviousSlot,
  next: NextSlot,
};
const ManageContentRetention = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const debouncedSearchCategoryText = useDebounce(searchTerm, 500);
  const [selectedItems, setSelectedItems] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };
  const role = useSelector((state) => state.auth?.loginDetails?.profile?.role);

  const token = localStorage.getItem("token");
  const request = useMemo(
    () => ({
      url: `/v1/web/event/list/all?page=${page}&limit=12`,
      method: "POST",
      body: { eventTitle: debouncedSearchCategoryText },
    }),
    [page, debouncedSearchCategoryText],
  );
  const { data, loading: listLoading } = useDashboardList(request, token);
  const eventData = data.results;
  const totalRecords = data.total;
  const loading = deleting || listLoading;

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePreviousClick = () => {
    if (page > 1) {
      setPage((page) => page - 1);
    }
  };

  const handleNextClick = () => {
    setPage((page) => page + 1);
  };
  const handleSelect = (item) => {
    if (selectedItems.includes(item)) {
      const filteredItems = selectedItems.filter(
        (selectedItem) => selectedItem !== item,
      );
      setSelectedItems(filteredItems);
    }
    if (!selectedItems.includes(item)) {
      setSelectedItems((prevSelectedItems) => [...prevSelectedItems, item]);
    }
  };

  const handleDeleteModalOpen = () => {
    if (selectedItems.length > 0) {
      setOpen(true);
    }
  };

  const handleDelete = () => {
    setDeleting(true);
    const payload = {
      eventIds: selectedItems?.map((item) => item?.id ?? item?._id),
    };
    baseFetch({
      url: `/v1/web/event/multiple-delete`,
      method: "POST",
      body: payload,
    })
      .then(() => {
        setDeleting(false);
        navigate("/dashboard");
      })
      .catch((err) => {
        setDeleting(false);
        if (err === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      });
  };
  return (
    <>
      <MenteeHeader
        role={role?.join(", ")}
        active={""}
        searchTerm={searchTerm}
        handleSearchChange={handleSearchChange}
      />
      <Background>
        {loading && <Loader />}
        <Box
          sx={{
            paddingBottom: {
              xs: "50px",
              sm: 0,
            },
          }}
          className="dashboard-main-div"
        >
          <Box
            sx={{
              padding: {
                xs: "10px 0 20px",
                sm: "10px 0 20px",
                md: "10px 0 20px",
                lg: "10px 0 20px",
                xl: "20px 0 30px",
              },
            }}
          >
            <Container maxWidth="xl">
              <Grid container spacing={{ lg: 3, sm: 2, xs: 1 }}>
                <Grid
                  item
                  xs={12}
                  sm={11}
                  md={10}
                  lg={10}
                  xl={9}
                  sx={{
                    overflow: "auto",
                    margin: "auto",
                  }}
                >
                  <Box
                    sx={{
                      width: {
                        xs: "650px",
                        sm: "auto",
                      },
                      overflowX: "auto",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: {
                        xs: "5px",
                        sm: "10px",
                        md: "10px",
                        lg: 3,
                      }, // Optional spacing between elements
                      scrollbarWidth: "none", // For Firefox
                      msOverflowStyle: "none", // For Internet Explorer and Edge
                      "&::-webkit-scrollbar": {
                        display: "none", // For Chrome, Safari, and other WebKit browsers
                      },
                    }}
                  >
                    {/* Left section with status buttons */}
                    <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: "#2A4876",
                          boxShadow: "0 4px 4px 0 #2A487633",
                          color: "#fff",
                          borderRadius: "50px",
                          height: "50px",
                          textTransform: "none",
                          fontSize: "18px",
                          fontWeight: 400,
                          padding: "5px 30px",
                          marginBottom: {
                            xs: 0,
                            sm: "10px",
                          },
                        }}
                      >
                        All Content
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box>
            <Container maxWidth="xl">
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={11}
                  md={10}
                  lg={10}
                  xl={9}
                  sx={{ margin: "auto" }}
                >
                  {!loading && (
                    <>
                      <Grid container spacing={{ lg: 3, sm: 2, xs: 2 }}>
                        {eventData?.length > 0 &&
                          eventData?.map((evt) => {
                            return (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={6}
                                lg={4}
                                key={evt?.id ?? evt?._id}
                              >
                                <Box>
                                  <Box
                                    onClick={() => handleSelect(evt)}
                                    sx={{
                                      width: "100%",
                                      cursor: "pointer",
                                      position: "relative",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "relative",
                                        overflow: "hidden",
                                        borderRadius: "20px",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          paddingLeft: "10px",
                                          paddingTop: "11px",
                                          position: "absolute",
                                          top: 0,
                                          left: 0,
                                          width: "100%",
                                        }}
                                      >
                                        <Box>
                                          <img src={Circle} />
                                          {selectedItems.includes(evt) && (
                                            <img
                                              src={tickMark}
                                              style={{
                                                position: "relative",
                                                left: "-26px",
                                                top: "-10px",
                                              }}
                                            />
                                          )}
                                        </Box>
                                        <Box
                                          sx={{
                                            position: "absolute",
                                            top: "10px",
                                            right: "10px",
                                            width: "auto",
                                            padding: "0 10px",
                                            height: "38px",
                                            borderRadius: "50px",
                                            background: "#fff",
                                            fontSize: {
                                              xs: "10px",
                                              sm: "10px",
                                              md: "12px",
                                              lg: "12px",
                                              xl: "14px",
                                            },
                                            fontWeight: 400,
                                            color: "#2A4876",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          {formatEventType(
                                            "key",
                                            evt?.eventType,
                                          )}
                                        </Box>
                                      </Box>
                                      <Box
                                        sx={{
                                          background:
                                            "linear-gradient(180deg, rgba(0, 0, 0, 0) 5.83%, #000000 100%)",
                                          width: "100%",
                                          height: "100%",
                                          position: "absolute",
                                          top: 0,
                                          left: 0,
                                        }}
                                      ></Box>
                                      <Box
                                        component="img"
                                        src={evt?.eventPhoto}
                                        alt="logo"
                                        sx={{
                                          width: "100%",
                                          height: {
                                            xs: "200px",
                                            sm: "200px",
                                            md: "250px",
                                            lg: "300px",
                                            xl: "300px",
                                          },
                                          borderRadius: "20px",
                                        }}
                                      />
                                    </Box>
                                    <Box
                                      sx={{
                                        width: "100%",
                                        position: "absolute",
                                        left: 0,
                                        bottom: 0,
                                        padding: {
                                          xs: "15px",
                                          sm: "15px",
                                          md: "15px",
                                          lg: "10px 10px 15px",
                                          xl: "20px",
                                        },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          paddingBottom: "10px",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            color: "#ffffff",
                                            fontSize: {
                                              xs: "16px",
                                              sm: "16px",
                                              md: "18px",
                                              lg: "18px",
                                              xl: "20px",
                                            },
                                            lineHeight: {
                                              xs: "20px",
                                              sm: "20px",
                                              md: "20px",
                                              lg: "20px",
                                              xl: "30px",
                                            },
                                            fontWeight: 600,
                                            textAlign: "left",
                                            textTransform: "capitalize",
                                          }}
                                        >
                                          {evt?.eventTitle}
                                        </Typography>
                                        {evt.eventFee && (
                                          <Box
                                            sx={{
                                              padding: "5px 10px",
                                              borderRadius: "10px",
                                              background: "#ffffff",
                                            }}
                                          >
                                            <Typography
                                              sx={{
                                                color: "#2A4876",
                                                fontSize: {
                                                  xs: "12px",
                                                  sm: "14px",
                                                  md: "14px",
                                                  lg: "16px",
                                                  xl: "16px",
                                                },
                                                textTransform: "capitalize",
                                                fontWeight: 600,
                                              }}
                                            >
                                              ${evt.eventFee}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                      {showDateAndTime(evt) && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            paddingBottom: "10px",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <Box
                                              component="img"
                                              src="/images/icons/time-circle-icon.png"
                                              alt="logo"
                                              sx={{
                                                width: {
                                                  xs: "18px",
                                                  sm: "18px",
                                                  md: "18px",
                                                  lg: "12px",
                                                  xl: "18px",
                                                },
                                                height: {
                                                  xs: "18px",
                                                  sm: "18px",
                                                  md: "18px",
                                                  lg: "12px",
                                                  xl: "18px",
                                                },
                                              }}
                                            />
                                            <Typography
                                              sx={{
                                                color: "#ffffff",
                                                fontSize: {
                                                  xs: "12px",
                                                  sm: "12px",
                                                  md: "12px",
                                                  lg: "12px",
                                                  xl: "16px",
                                                },
                                                fontWeight: 400,
                                                paddingLeft: {
                                                  xs: "10px",
                                                  sm: "10px",
                                                  md: "10px",
                                                  lg: "5px",
                                                  xl: "10px",
                                                },
                                                textAlign: "left",
                                              }}
                                            >
                                              {evt?.eventTimeStart}-
                                              {evt?.eventTimeEnd}
                                            </Typography>
                                          </Box>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              paddingLeft: {
                                                xs: "10px",
                                                sm: "10px",
                                                md: "10px",
                                                lg: "15px",
                                                xl: "20px",
                                              },
                                            }}
                                          >
                                            <Box
                                              component="img"
                                              src="/images/icons/calendar-icon-white.png"
                                              alt="logo"
                                              sx={{
                                                width: {
                                                  xs: "18px",
                                                  sm: "18px",
                                                  md: "18px",
                                                  lg: "12px",
                                                  xl: "18px",
                                                },
                                                height: {
                                                  xs: "18px",
                                                  sm: "18px",
                                                  md: "18px",
                                                  lg: "12px",
                                                  xl: "18px",
                                                },
                                              }}
                                            />
                                            <Typography
                                              sx={{
                                                color: "#ffffff",
                                                fontSize: {
                                                  xs: "12px",
                                                  sm: "12px",
                                                  md: "12px",
                                                  lg: "12px",
                                                  xl: "16px",
                                                },
                                                fontWeight: 400,
                                                paddingLeft: {
                                                  xs: "10px",
                                                  sm: "10px",
                                                  md: "10px",
                                                  lg: "5px",
                                                  xl: "10px",
                                                },
                                                textAlign: "left",
                                              }}
                                            >
                                              {evt?.eventDateStart
                                                ? new Date(
                                                    evt.eventDateStart,
                                                  ).toLocaleDateString(
                                                    "en-US",
                                                    {
                                                      month: "short",
                                                      day: "2-digit",
                                                      year: "numeric",
                                                      timeZone:
                                                        evt?.timezone || "UTC",
                                                    },
                                                  )
                                                : ""}
                                              -{" "}
                                              {evt?.eventDateEnd
                                                ? new Date(
                                                    evt.eventDateEnd,
                                                  ).toLocaleDateString(
                                                    "en-US",
                                                    {
                                                      month: "short",
                                                      day: "2-digit",
                                                      year: "numeric",
                                                      timeZone:
                                                        evt?.timezone || "UTC",
                                                    },
                                                  )
                                                : ""}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              </Grid>
                            );
                          })}
                      </Grid>
                      {totalRecords > 12 && (
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: {
                              xs: "15px 0",
                              sm: "30px",
                              md: "30px",
                              lg: "40px",
                            },
                            "& ul li:last-child": {
                              position: {
                                xs: "unset",
                                md: "absolute",
                              },
                              right: "10%",
                            },
                            "& ul li:first-child": {
                              position: {
                                xs: "unset",
                                md: "absolute",
                              },
                              left: "10%",
                            },
                          }}
                        >
                          <Pagination
                            count={Math.ceil(totalRecords / 12)}
                            page={page}
                            onChange={handlePageChange}
                            renderItem={(item) => (
                              <PaginationItem
                                {...item}
                                slots={paginationSlots}
                                slotProps={{
                                  previous: { onClick: handlePreviousClick },
                                  next: {
                                    onClick: handleNextClick,
                                    disabled:
                                      page === Math.ceil(totalRecords / 12),
                                  },
                                }}
                              />
                            )}
                          />
                        </Box>
                      )}
                      <Box
                        sx={{
                          position: "fixed",
                          bottom: 0,
                          left: 0,
                          width: "100%",
                          background: "transparent",
                          zIndex: "99",
                          display: "flex",
                          gap: "10px",
                          justifyContent: "center",
                          paddingBottom: "15px",
                        }}
                      >
                        <Button
                          className="text-white"
                          variant="contained"
                          sx={{
                            backgroundColor: "#7BB9D5",
                            fontSize: {
                              xs: "16px",
                              sm: "16px",
                              md: "18px",
                            },
                            width: {
                              xs: "45%",
                              sm: "45%",
                              md: "25%",
                            },
                            height: {
                              xs: "40px",
                              sm: "45px",
                              md: "45px",
                              lg: "50px",
                              xl: "60px",
                            },
                            borderRadius: "100px",
                            textTransform: "none",
                            color: "#152134 !important",
                            boxShadow: "none",
                          }}
                          onClick={() => navigate("/profile/content-retention")}
                        >
                          Cancel
                        </Button>
                        <Button
                          className="text-white"
                          variant="contained"
                          sx={{
                            backgroundColor: "#2A4876",
                            "&:disabled": {
                              backgroundColor: "#AAB6C8",
                            },
                            fontWeight: 700,
                            fontSize: {
                              xs: "16px",
                              sm: "16px",
                              md: "18px",
                            },
                            width: {
                              xs: "45%",
                              sm: "45%",
                              md: "25%",
                            },
                            height: {
                              xs: "40px",
                              sm: "45px",
                              md: "45px",
                              lg: "50px",
                              xl: "60px",
                            },
                            borderRadius: "100px",
                            textTransform: "none",
                          }}
                          disabled={selectedItems.length === 0}
                          onClick={handleDeleteModalOpen}
                        >
                          Delete
                          {` (${selectedItems.length} selected)`}
                        </Button>
                      </Box>
                    </>
                  )}
                </Grid>
              </Grid>
            </Container>
          </Box>
        </Box>
        {open && (
          <DeleteEventPopup
            handleConfirm={() => handleDelete()}
            handleClose={() => setOpen(false)}
            open={open}
            contentRetention={true}
          />
        )}
      </Background>
    </>
  );
};

export default ManageContentRetention;
